import React, { useEffect, useRef, useState } from "react";
import { MagnifyingGlass, FunnelSimple, CircleNotch } from "@phosphor-icons/react";
import { api } from "../../lib/api";

/**
 * Compact filter bar shown above the scanner content area. Holds search,
 * min-volume slider and sort toggles. Tabs have moved into the
 * `DashboardSidebar` component for the new left-nav layout.
 *
 * Search behaviour:
 *   1. The text input still filters the visible signal list (existing
 *      behaviour) so users can narrow the breakout list quickly.
 *   2. A debounced server query against `/api/search-symbols` shows an
 *      autocomplete dropdown of ANY Nifty-500 stock (signal or not).
 *      Clicking a row opens the StockDetailModal directly via
 *      `onOpenSymbol`, letting the user analyse any chart without first
 *      needing a breakout signal for it.
 */
export default function FilterBar({
  search, setSearch,
  minVol, setMinVol,
  sort, setSort,
  visible = true,
  onOpenSymbol,
}) {
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const wrapRef = useRef(null);

  // Debounced autocomplete fetch.
  useEffect(() => {
    if (!search || search.trim().length < 1) { setResults([]); return; }
    const q = search.trim();
    const t = setTimeout(() => {
      setLoading(true);
      api.get(`/search-symbols?q=${encodeURIComponent(q)}&limit=15`)
        .then(({ data }) => setResults(Array.isArray(data?.results) ? data.results : []))
        .catch(() => setResults([]))
        .finally(() => setLoading(false));
    }, 180);
    return () => clearTimeout(t);
  }, [search]);

  // Click outside closes the dropdown.
  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open]);

  if (!visible) return null;

  const handleOpenSymbol = (row) => {
    setOpen(false);
    setSearch("");
    if (typeof onOpenSymbol === "function") {
      onOpenSymbol({
        symbol: row.symbol,
        company_name: row.company_name,
        // Lookup-only signal — chart loads, no Entry/SL/Target lines.
        entry: null, stop_loss: null, target: null,
        score: 0,
        signal_type: "lookup",
        _lookupOnly: true,
      });
    }
  };

  return (
    <div className="flex flex-col md:flex-row md:items-center gap-3 px-6 py-3 border-b border-subtle bg-surface/40">
      <div ref={wrapRef} className="relative flex-1 max-w-lg">
        <div className="flex items-center gap-2 px-3 py-1.5 border border-subtle rounded-sm bg-app">
          <MagnifyingGlass size={14} className="text-muted-fg" />
          <input
            data-testid="search-input"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
            placeholder="Search NSE stock or filter signals…"
            className="w-full bg-transparent outline-none text-sm font-mono text-primary-fg placeholder:text-muted-fg"
          />
          {loading && <CircleNotch size={12} className="text-muted-fg animate-spin" />}
        </div>
        {open && search.trim().length > 0 && (
          <div
            data-testid="search-dropdown"
            className="absolute left-0 right-0 mt-1 z-40 max-h-[360px] overflow-y-auto border border-subtle rounded-sm bg-surface shadow-2xl shadow-black/40"
          >
            {results.length === 0 && !loading ? (
              <div className="px-3 py-3 text-[10px] font-mono uppercase tracking-widest text-muted-fg">
                No NSE stocks match "{search}"
              </div>
            ) : (
              <>
                <div className="sticky top-0 px-3 py-1.5 text-[9px] font-mono uppercase tracking-[0.2em] text-muted-fg bg-surface/95 backdrop-blur border-b border-subtle">
                  Open chart for any NSE stock
                </div>
                {results.map((r) => (
                  <button
                    type="button"
                    key={r.symbol}
                    onClick={() => handleOpenSymbol(r)}
                    data-testid={`search-result-${r.display}`}
                    className="w-full flex items-center justify-between gap-3 px-3 py-2 hover:bg-surface-hover transition text-left"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="font-mono text-[12px] font-semibold text-primary-fg shrink-0">
                        {r.display}
                      </span>
                      <span className="text-[10px] text-muted-fg truncate">
                        {r.company_name && r.company_name !== r.display ? r.company_name : ""}
                      </span>
                    </div>
                    {r.has_signal && (
                      <span className="shrink-0 px-1.5 py-0.5 border border-pos/40 rounded-sm bg-pos-soft text-pos text-[9px] font-mono uppercase tracking-widest">
                        Signal
                      </span>
                    )}
                  </button>
                ))}
              </>
            )}
          </div>
        )}
      </div>
      <div className="flex items-center gap-2 px-3 py-1.5 border border-subtle rounded-sm bg-app">
        <FunnelSimple size={14} className="text-muted-fg" />
        <span className="text-[10px] font-mono uppercase tracking-widest text-muted-fg">Min Vol</span>
        <input
          data-testid="min-volume-slider"
          type="range" min="1" max="10" step="0.5"
          value={minVol}
          onChange={(e) => setMinVol(parseFloat(e.target.value))}
          className="w-28 accent-emerald-500"
        />
        <span className="font-mono text-sm text-primary-fg tabular-nums">{minVol}×</span>
      </div>
      <div className="flex items-center gap-2">
        {["volume", "strength", "recent", "pct_change"].map((k) => (
          <button
            key={k}
            data-testid={`sort-${k}`}
            onClick={() => setSort(k)}
            className={
              "px-3 py-1.5 border border-subtle rounded-sm text-[10px] font-mono uppercase tracking-widest transition " +
              (sort === k
                ? "border-transparent"
                : "text-secondary-fg hover:bg-surface-hover hover:text-primary-fg")
            }
            style={sort === k ? { background: "hsl(var(--text-primary))", color: "hsl(var(--bg))" } : undefined}
          >
            {k === "pct_change" ? "% Change" : k}
          </button>
        ))}
      </div>
    </div>
  );
}
